<template>
  <aside class="sidebar" :class="{ collapsed: isCollapsed }">
    <div class="sidebar-header">
      <router-link to="/" class="sidebar-brand">
        <img src="/icon.png" alt="SOLUTIONS PRÉFA Logo" class="sidebar-logo" />
        <span class="sidebar-title">SOLUTIONS PRÉFA</span>
      </router-link>
    </div>
    <nav class="sidebar-nav">
      <ul class="nav-list">
        <li v-for="item in navItems" :key="item.path" class="nav-item">
          <router-link
            :to="item.path"
            class="nav-link"
            :class="{ active: $route.path === item.path }"
          >
            <span class="nav-icon" v-html="item.icon"></span>
            <span class="nav-label">{{ item.label }}</span>
          </router-link>
        </li>
      </ul>
    </nav>

    <div v-if="user" class="sidebar-footer">
      <div class="sidebar-user">
        <div class="sidebar-user-header">
          <button
            class="sidebar-user-settings-btn"
            @click="showChangePasswordModal = true"
            title="Paramètres (changer le mot de passe)"
            aria-label="Paramètres (changer le mot de passe)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
          </button>
          <div class="sidebar-user-info">
            <div class="sidebar-user-name">{{ user.nom }}</div>
            <div class="sidebar-user-email">{{ user.email }}</div>
          </div>
        </div>
        <button class="sidebar-logout-btn" @click="handleLogout" title="Se déconnecter">
          <svg class="sidebar-logout-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          <span class="sidebar-logout-label">Déconnexion</span>
        </button>
      </div>
    </div>

    <button class="sidebar-toggle" @click="toggleCollapse" :title="isCollapsed ? 'Agrandir' : 'Réduire'">
      <svg v-if="isCollapsed" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
      <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
    </button>

    <!-- Modal changement de mot de passe -->
    <FormPage v-if="showChangePasswordModal" @close="closePasswordModal">
      <template #title>Changer le mot de passe</template>
      <form @submit.prevent="handleChangePassword" class="change-password-form">
        <BaseInput
          v-model.trim="passwordForm.currentPassword"
          label="Mot de passe actuel"
          type="password"
          placeholder="••••••••"
          required
        />
        <BaseInput
          v-model.trim="passwordForm.newPassword"
          label="Nouveau mot de passe"
          type="password"
          placeholder="••••••••"
          required
        />
        <BaseInput
          v-model.trim="passwordForm.confirmPassword"
          label="Confirmer le nouveau mot de passe"
          type="password"
          placeholder="••••••••"
          required
        />
        <div v-if="passwordError" class="error-message">{{ passwordError }}</div>
        <div v-if="passwordSuccess" class="success-message">{{ passwordSuccess }}</div>
        <div class="form-actions">
          <BaseButton type="button" variant="ghost" @click="closePasswordModal">Annuler</BaseButton>
          <BaseButton type="submit" variant="primary" :loading="changingPassword">
            Enregistrer
          </BaseButton>
        </div>
      </form>
    </FormPage>
  </aside>
</template>

<script setup lang="ts">
import { ref, computed, reactive } from 'vue';
import { RouterLink, useRouter } from 'vue-router';
import { useAuth } from '../composables/useAuth';
import FormPage from './FormPage.vue';
import BaseInput from './BaseInput.vue';
import BaseButton from './BaseButton.vue';
import { apiFetch } from '../utils/api';

const { user, logout } = useAuth();
const router = useRouter();
const isCollapsed = ref(false);
const showChangePasswordModal = ref(false);
const changingPassword = ref(false);
const passwordError = ref<string | null>(null);
const passwordSuccess = ref<string | null>(null);

const passwordForm = reactive({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
});

function toggleCollapse() {
  isCollapsed.value = !isCollapsed.value;
}

async function handleLogout() {
  await logout();
  router.push({ name: 'login' });
}

function closePasswordModal() {
  showChangePasswordModal.value = false;
  passwordForm.currentPassword = '';
  passwordForm.newPassword = '';
  passwordForm.confirmPassword = '';
  passwordError.value = null;
  passwordSuccess.value = null;
}

async function handleChangePassword() {
  passwordError.value = null;
  passwordSuccess.value = null;

  if (passwordForm.newPassword !== passwordForm.confirmPassword) {
    passwordError.value = 'Les mots de passe ne correspondent pas';
    return;
  }

  if (passwordForm.newPassword.length < 8) {
    passwordError.value = 'Le mot de passe doit contenir au moins 8 caractères';
    return;
  }

  changingPassword.value = true;
  try {
    await apiFetch('/authentification/change-password', {
      method: 'POST',
      body: {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      }
    });
    passwordSuccess.value = 'Mot de passe modifié avec succès';
    setTimeout(() => {
      closePasswordModal();
    }, 1500);
  } catch (err) {
    passwordError.value = err instanceof Error ? err.message : 'Erreur lors du changement de mot de passe';
  } finally {
    changingPassword.value = false;
  }
}

// SVG icons
const icons = {
  bureau: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>`,
  usine: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>`,
  logistique: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>`,
  chantier: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 18a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v2z"/><path d="M10 10V5l-3 3"/><path d="M14 10V5l3 3"/><path d="M4 15v-3a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v3"/></svg>`,
  users: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`
};

// Items de navigation selon le rôle
const navItems = computed(() => {
  const role = user.value?.role || '';
  const allItems = [
    { path: '/bureau', label: 'Bureau', icon: icons.bureau, roles: ['ADMIN', 'BUREAU', 'OFFICE'] },
    { path: '/usine', label: 'Usine', icon: icons.usine, roles: ['ADMIN', 'USINE', 'FACTORY'] },
    { path: '/site-chantier', label: 'Site / Chantier', icon: icons.chantier, roles: ['ADMIN', 'CHANTIER', 'SUPERVISION', 'SITE'] },
    { path: '/users', label: 'Utilisateurs', icon: icons.users, roles: ['ADMIN'] }
  ];

  if (role === 'ADMIN') {
    return allItems;
  }

  return allItems.filter(item => item.roles.includes(role));
});
</script>

<style scoped>
.sidebar {
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  width: 240px;
  background: var(--color-bg-sidebar);
  border-right: none;
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
  z-index: 100;
  box-shadow: 4px 0 16px rgba(0,0,0,0.15);
}

.sidebar.collapsed {
  width: 60px;
}

.sidebar-header {
  padding: var(--spacing-lg);
  border-bottom: 1px solid rgba(255,255,255,0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}

.sidebar-brand {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  text-decoration: none;
  transition: opacity var(--transition-base);
}

.sidebar-brand:hover {
  opacity: 0.8;
}

.sidebar-logo {
  width: 40px;
  height: 40px;
  object-fit: contain;
  flex-shrink: 0;
}

.sidebar-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: #ffffff;
  white-space: nowrap;
  transition: opacity var(--transition-base);
  line-height: 1.3;
  letter-spacing: 0.02em;
}

.sidebar.collapsed .sidebar-title {
  opacity: 0;
  width: 0;
  overflow: hidden;
}

.sidebar-nav {
  flex: 1;
  overflow-y: auto;
  padding: var(--spacing-md) 0;
}

.nav-list {
  list-style: none;
  margin: 0;
  padding: 0 var(--spacing-sm);
}

.nav-item {
  margin: 2px 0;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: 0.6rem var(--spacing-md);
  color: rgba(255,255,255,0.55);
  text-decoration: none;
  transition: all var(--transition-fast);
  border-left: 2px solid transparent;
  border-radius: var(--radius-md);
}

.nav-link:hover {
  background: rgba(255,255,255,0.06);
  color: rgba(255,255,255,0.9);
}

.nav-link.active {
  background: rgba(255,255,255,0.08);
  color: #ffffff;
  border-left-color: #60a5fa;
  font-weight: var(--font-weight-semibold);
}

.nav-icon {
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.nav-label {
  flex: 1;
  font-size: var(--font-size-md);
  white-space: nowrap;
}

.sidebar.collapsed .nav-label {
  display: none;
}

.sidebar.collapsed .nav-list {
  padding: 0 var(--spacing-xs);
}

.sidebar.collapsed .nav-link {
  justify-content: center;
  padding: 0.6rem;
  border-left-color: transparent;
}

.sidebar.collapsed .nav-link.active {
  border-bottom: 2px solid #60a5fa;
}

.sidebar-footer {
  padding: var(--spacing-md);
  border-top: 1px solid rgba(255,255,255,0.08);
  background: rgba(0,0,0,0.15);
}

.sidebar-user {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.sidebar-user-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding-bottom: var(--spacing-sm);
  border-bottom: 1px solid rgba(255,255,255,0.08);
}

.sidebar-user-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
}

.sidebar-user-name {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: rgba(255,255,255,0.9);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-user-email {
  font-size: var(--font-size-xs);
  color: rgba(255,255,255,0.4);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar.collapsed .sidebar-user-info {
  display: none;
}

.sidebar.collapsed .sidebar-user-header {
  justify-content: center;
  padding-bottom: 0;
  border-bottom: none;
}

.sidebar-user-settings-btn {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.55);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.sidebar-user-settings-btn:hover {
  background: rgba(255,255,255,0.14);
  color: rgba(255,255,255,0.9);
}

.sidebar.collapsed .sidebar-user-settings-btn {
  width: 40px;
  height: 40px;
}

.sidebar-logout-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: rgba(220,38,38,0.15);
  color: #fca5a5;
  border: 1px solid rgba(220,38,38,0.25);
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  transition: all var(--transition-fast);
}

.sidebar-logout-icon {
  flex-shrink: 0;
}

.sidebar-logout-btn:hover {
  background: rgba(220,38,38,0.25);
  color: #fecaca;
}

.sidebar-logout-label {
  text-align: center;
  white-space: nowrap;
}

.sidebar.collapsed .sidebar-logout-label {
  display: none;
}

.sidebar.collapsed .sidebar-logout-btn {
  padding: var(--spacing-sm);
  min-width: 32px;
}

.change-password-form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.error-message {
  padding: var(--spacing-sm);
  background: #fee2e2;
  border: 1px solid #fca5a5;
  border-radius: var(--radius-md);
  color: #991b1b;
  font-size: var(--font-size-sm);
}

.success-message {
  padding: var(--spacing-sm);
  background: #d1fae5;
  border: 1px solid #6ee7b7;
  border-radius: var(--radius-md);
  color: #065f46;
  font-size: var(--font-size-sm);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-md);
  margin-top: var(--spacing-lg);
}

.sidebar-toggle {
  padding: var(--spacing-md);
  background: rgba(255,255,255,0.04);
  border: none;
  border-top: 1px solid rgba(255,255,255,0.08);
  cursor: pointer;
  color: rgba(255,255,255,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-fast);
}

.sidebar-toggle:hover {
  background: rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.7);
}

/* Scrollbar styling */
.sidebar-nav::-webkit-scrollbar {
  width: 4px;
}

.sidebar-nav::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-nav::-webkit-scrollbar-thumb {
  background: rgba(255,255,255,0.15);
  border-radius: var(--radius-full);
}

.sidebar-nav::-webkit-scrollbar-thumb:hover {
  background: rgba(255,255,255,0.25);
}

</style>
